import { toast, ToastOptions } from 'react-toastify';
import { LocalizedType } from '@/types/common';
import { ActivityTypes, SkillTypes } from '@/types/skill';
import { Topic } from '@/types/topic';
import { Quiz } from '@/types/quiz';
import config from '@/config';
import { Question } from '@/types/question';

import avatarImages from './avatarImages';
import { EMAIL_FORMAT_REGEX } from './emailRegex';

import speak from '@/assets/svg/skills/speak.svg';
import listen from '@/assets/svg/skills/listen.svg';
import read from '@/assets/svg/skills/read.svg';
import grammar from '@/assets/svg/skills/grammar.svg';
import spelling from '@/assets/svg/skills/spelling.svg';
import vocabulary from '@/assets/svg/skills/vocabulary.svg';
import naturalConversation from '@/assets/svg/skills/naturalConversation.svg';
import openWriting from '@/assets/svg/skills/openWriting.svg';
import memory from '@/assets/svg/skills/memory.svg';
import imageToText from '@/assets/svg/skills/imageToText.svg';
import textToImage from '@/assets/svg/skills/textToImage.svg';
import textToText from '@/assets/svg/skills/textToText.svg';
import unitImages from '@/assets/svg/skills';

import stars1 from '@/assets/images/stars-1.png';
import stars2 from '@/assets/images/stars-2.png';
import stars3 from '@/assets/images/stars-3.png';
import stars0 from '@/assets/images/stars-0.png';

import speakIcon from '@/assets/svg/speak-icon.svg';
import listenIcon from '@/assets/svg/listen-icon.svg';
import readIcon from '@/assets/svg/read-icon.svg';
import spellingIcon from '@/assets/svg/spelling-icon.svg';
import grammarIcon from '@/assets/svg/grammar-icon.svg';
import vocabularyIcon from '@/assets/svg/vocabulary-icon.svg';

import diamondBlue from '@/assets/svg/diamond-blue.svg';
import diamondGold from '@/assets/svg/diamond-gold.svg';
import diamondSilver from '@/assets/svg/diamond-silver.svg';

import magnifyingGlass from '@/assets/svg/magnifying-glass.svg';
import brain from '@/assets/svg/brain.svg';
import flag from '@/assets/svg/flag-red.svg';

import en from '@/assets/svg/langs/en.svg';
import he from '@/assets/svg/langs/he.svg';
import pt from '@/assets/svg/langs/pt.svg';
import es from '@/assets/svg/langs/es.svg';
import { GameTypes, NewGameTypes } from '@/types/game';
import { LevelSettingItem, UserState } from '@/types/user';
import { uiActions } from '@/store/reducers/ui';
import store from '@/store';
import { TFunction } from 'i18next';
import * as XLSX from 'xlsx';
import { ClassOverviewData } from '@/types/progress';
import { formatMinutes } from '@/utils/formatters';
import { formatPersonName } from '@/utils/personName';
import dayjs from 'dayjs';

export const skillsColors = {
  [SkillTypes.Speak]: '#05abf4',
  [SkillTypes.Listen]: '#e11560',
  [SkillTypes.Read]: '#973d9d',
  [SkillTypes.Spelling]: '#2bae19',
  [SkillTypes.Grammar]: '#ff9f4c',
  [SkillTypes.Vocabulary]: '#145c99',
};

const badges = [
  {
    type: SkillTypes.Grammar,
    color: 'orange-regular',
    backgroundColor: 'orange-light',
  },
  {
    type: SkillTypes.Speak,
    color: 'blue-regular',
    backgroundColor: 'blue-light',
  },
  {
    type: SkillTypes.Spelling,
    color: 'green-regular',
    backgroundColor: 'green-light',
  },
  {
    type: SkillTypes.Read,
    color: 'purple-regular',
    backgroundColor: 'purple-light',
  },
  {
    type: SkillTypes.Listen,
    color: 'pink-regular',
    backgroundColor: 'pink-light',
  },
  {
    type: SkillTypes.Vocabulary,
    color: 'blue-light9',
    backgroundColor: 'blue-dark',
  },
];

const progressColors = [
  {
    type: SkillTypes.Speak,
    trailColor: 'blue-light4',
    pathColor: 'blue-regular',
  },
  {
    type: SkillTypes.Listen,
    trailColor: 'pink-light2',
    pathColor: 'pink-regular',
  },
  {
    type: SkillTypes.Read,
    trailColor: 'purple-light2',
    pathColor: 'purple-regular',
  },
  {
    type: SkillTypes.Spelling,
    trailColor: 'green-light2',
    pathColor: 'green-regular',
  },
  {
    type: SkillTypes.Grammar,
    trailColor: 'orange-light2',
    pathColor: 'orange-regular',
  },
  {
    type: SkillTypes.Vocabulary,
    trailColor: 'blue-light9',
    pathColor: 'blue-dark',
  },
];

const newProgressColors = [
  {
    id: 1,
    trailColor: 'orange-light2',
    pathColor: 'orange-regular',
  },
  {
    id: 2,
    trailColor: 'green-light2',
    pathColor: 'green-regular',
  },
  {
    id: 3,
    trailColor: 'pink-light2',
    pathColor: 'pink-regular',
  },
  {
    id: 4,
    trailColor: 'blue-light9',
    pathColor: 'blue-regular',
  },
  {
    id: 5,
    trailColor: 'purple-light2',
    pathColor: 'purple-regular',
  },
  {
    id: 6,
    trailColor: 'blue-light2',
    pathColor: 'blue-dark',
  },
];

const achievements = [
  {
    level: 1,
    img: diamondBlue,
  },
  {
    level: 2,
    img: diamondGold,
  },
  {
    level: 3,
    img: diamondSilver,
  },
  {
    level: 4,
    img: diamondSilver,
  },
  {
    level: 5,
    img: diamondSilver,
  },
];

const levelTestQuizzes = [
  {
    id: 1,
    img: magnifyingGlass,
    name: 'letsBegin',
  },
  {
    id: 2,
    img: brain,
    name: 'keepGoing',
  },
  {
    id: 3,
    img: flag,
    name: 'letsFinish',
  },
];

const skillImages = {
  speak,
  listen,
  read,
  grammar,
  spelling,
  vocabulary,
  openWriting,
  naturalConversation,
  memory,
  imageToText,
  textToImage,
  textToText,
};

const starsImages = [
  {
    amount: 3,
    img: stars3,
  },
  {
    amount: 2,
    img: stars2,
  },
  {
    amount: 1,
    img: stars1,
  },
  {
    amount: 0,
    img: stars0,
  },
];

const avatarBackgroundColors = ['#64CDFC', '#FC649C', '#F264FC', '#FCAA64', '#5BE548', '#64B6FC'];

const languageIcons = [
  {
    lang: 'en',
    icon: en,
  },
  {
    lang: 'he',
    icon: he,
  },
  {
    lang: 'pt',
    icon: pt,
  },
  {
    lang: 'es',
    icon: es,
  },
];

const supportedLanguages = ['en', 'es', 'pt', 'he', 'ar'];

export const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.{}\[\]()?\-\"!@#%&/\\,><':;|_~`+=])[A-Za-z\d\^$*.{}\[\]()?\-\"!@#%&/\\,><':;|_~`+=]{8,}$/;

const commonUtils = {
  sleep: (ms: number) => {
    return new Promise(r => setTimeout(r, ms));
  },
  isIOS: () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  },
  showToast: (message: string, options: ToastOptions = {}) => {
    toast(message, {
      position: 'bottom-center',
      autoClose: 4000,
      closeButton: false,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      ...options,
    });
  },
  calcPercentage: (actual: number, total: number) => {
    const percent = (100 * actual) / total;

    return isNaN(percent) ? 0 : Math.round(percent);
  },
  roundNumber: (num: number, decimalPlaces: number = 0) => {
    const decimal = Math.pow(10, decimalPlaces);
    return Math.round(num * decimal) / decimal;
  },
  isSimilar: (sourceString: string, incomingString: string) => {
    if (!incomingString) {
      return false;
    }

    if ((sourceString || '').toLowerCase() === (incomingString || '').toLowerCase()) {
      return true;
    }

    const sourceWords = sourceString.replace(/[^\w\s]+/g, '').split(' ');
    const incomingWords = incomingString.replace(/[^\w\s]+/g, '').split(' ');

    let correctOrderCount = 0;

    for (let i = 0; i < incomingWords.length; i++) {
      if (sourceWords[i] === incomingWords[i]) {
        correctOrderCount++;
      }
    }

    return sourceWords.length - correctOrderCount <= 1;
  },
  isSimilarPhrases: (sourceString: string, incomingString: string) => {
    if (!incomingString) {
      return false;
    }

    if ((sourceString || '').toLowerCase() === (incomingString || '').toLowerCase()) {
      return true;
    }

    let pattern = sourceString
      .toLowerCase()
      .split('/')
      .map(p =>
        p
          .replace(/[^\w\s]+/g, '')
          .replace(/[\s]+/g, ' ')
          .replace(/[_]+/g, '(.)+')
          .trim()
      )
      .map(p => `(${p})`)
      .join('|');
    pattern = `^${pattern}$`;
    const regExp = new RegExp(pattern);

    const incomingWords = incomingString
      .toLowerCase()
      .replace(/[^\w\s]+/g, '')
      .replace(/[_]+/g, ' ')
      .replace(/[\s]+/g, ' ')
      .trim();

    return regExp.test(incomingWords);
  },
  groupBySize: <T>(items: T[], size: number) => {
    const nestedArrays = [];

    for (let i = 0; i < items.length; i += size) {
      const subArray = items.slice(i, i + size);
      nestedArrays.push(subArray);
    }

    return [...nestedArrays];
  },
  shuffleArray: <T>(array: T[]) => {
    const newArray = [...array];

    for (let i = newArray.length - 1; i > 0; i--) {
      const randomIndex = Math.floor(Math.random() * (i + 1));

      [newArray[i], newArray[randomIndex]] = [newArray[randomIndex], newArray[i]];
    }

    return newArray;
  },
  getNumberWithSuffix: (number: number) => {
    let suffix;

    if (number % 10 === 1 && number % 100 !== 11) {
      suffix = 'st';
    } else if (number % 10 === 2 && number % 100 !== 12) {
      suffix = 'nd';
    } else if (number % 10 === 3 && number % 100 !== 13) {
      suffix = 'rd';
    } else {
      suffix = 'th';
    }

    return number + suffix;
  },
  getName: (model: LocalizedType, isLocal = false) => {
    if (isLocal) {
      const nameLocal = model.nameLocal;

      return nameLocal != null && nameLocal.length > 0 ? nameLocal : model.name;
    }

    return model.name;
  },
  getQuestionInstructions: (
    question: Question,
    nativeLanguage: string | null,
    quiz?: Quiz | null
  ) => {
    if (question?.instructions) {
      return {
        instructions: question.instructions,
        isInstructionsInLocalLanguage: question.isInstructionsInLocalLanguage,
        direction:
          ['he', 'ar'].includes(nativeLanguage || '') && question.isInstructionsInLocalLanguage
            ? 'rtl'
            : 'ltr',
      };
    }

    if (quiz) {
      if (typeof quiz.isInstructionsInLocalLanguage === 'boolean') {
        return quiz.isInstructionsInLocalLanguage
          ? {
              instructions: quiz.instructionsLocal,
              isInstructionsInLocalLanguage: true,
              direction: ['he', 'ar'].includes(nativeLanguage || '') ? 'rtl' : 'ltr',
            }
          : {
              instructions: quiz.instructions,
              isInstructionsInLocalLanguage: false,
              direction: 'ltr',
            };
      }

      return {
        instructions: quiz.instructions,
        isInstructionsInLocalLanguage: false,
        direction: 'ltr',
      };
    }

    return {
      instructions: '',
      isInstructionsInLocalLanguage: false,
      direction: 'ltr',
    };
  },
  getQuestionTip: (question: Question, isLocal = false): string => {
    const newData = question?.newData?.data;
    if (!newData) return '';

    const { questionDataLocal, questionData } = newData;

    if (isLocal && questionDataLocal?.tipLocal) {
      return questionDataLocal.tipLocal || questionData?.tip || '';
    }

    return questionData?.tip || '';
  },
  getNewProgressColors: (id: number) => {
    return newProgressColors.find(i => i.id === id);
  },
  skillImage: (type: SkillTypes | ActivityTypes) => {
    return skillImages[type];
  },
  unitImage: (type: any, unitOrder?: number) => {
    return unitImages[`${type}${unitOrder}`];
  },
  activityImage: (type: NewGameTypes | GameTypes | null, unitOrder: number) => {
    const defaultData = commonUtils.unitImage(ActivityTypes.Grammar);

    switch (type) {
      case NewGameTypes.OpenWriting:
        return commonUtils.unitImage(ActivityTypes.OpenWriting, unitOrder);
      case NewGameTypes.Memory:
        return commonUtils.unitImage(ActivityTypes.Memory, unitOrder);
      case NewGameTypes.AudioToImage:
        return commonUtils.unitImage(ActivityTypes.Grammar, unitOrder);
      case NewGameTypes.NaturalConversation:
        return commonUtils.unitImage(ActivityTypes.NaturalConversation, unitOrder);
      case NewGameTypes.ImageToText:
        return commonUtils.unitImage(ActivityTypes.ImageToText, unitOrder);
      case NewGameTypes.TextToImage:
        return commonUtils.unitImage(ActivityTypes.TextToImage, unitOrder);
      case NewGameTypes.TextToText:
        return commonUtils.unitImage(ActivityTypes.TextToText, unitOrder);
      case NewGameTypes.Read:
        return commonUtils.unitImage(ActivityTypes.Read, unitOrder);
      case NewGameTypes.Grammar:
        return commonUtils.unitImage(ActivityTypes.TextToText, unitOrder);
      case NewGameTypes.Vocabulary:
      case GameTypes.Practice:
        return commonUtils.unitImage(ActivityTypes.Vocabulary, unitOrder);
      case GameTypes.ClosedSpelling:
        return commonUtils.unitImage(ActivityTypes.Spelling, unitOrder);
      case NewGameTypes.Listen:
        return commonUtils.unitImage(ActivityTypes.Listen, unitOrder);
      case GameTypes.Pronunciation:
        return commonUtils.unitImage(ActivityTypes.Speak, unitOrder);

      default:
        return defaultData;
    }
  },
  getLevelTestQuiz: (id: number) => {
    return levelTestQuizzes.find(quiz => quiz.id === id);
  },
  getStarImage: (amount: number) => {
    return starsImages.find(star => star.amount === amount);
  },
  getAvatarImages: () => {
    return avatarImages;
  },
  getAvatarImage: (key: string) => {
    return Object.entries(avatarImages).find(entry => entry[0] === key)?.[1];
  },
  getAvatarBackgroundColors: () => {
    return avatarBackgroundColors;
  },
  getLanguageIcon: (lang: string) => {
    return languageIcons.find(i => i.lang === lang)?.icon ?? '';
  },
  fixGradeSymbol: (grade: string) => {
    if (isNaN(+grade)) {
      return parseInt(grade).toString();
    }

    return grade;
  },
  getGradeText: (grade: string | number | null | undefined, t: TFunction): string => {
    if (!grade) {
      return '';
    }

    let gradeId = grade.toString().toLowerCase().trim();
    gradeId = gradeId === 'university' ? '13' : gradeId === 'none' ? '14' : gradeId;

    return t(`grade_${gradeId}`);
  },
  skillByName: (name: string): SkillTypes => {
    return SkillTypes[name as keyof typeof SkillTypes];
  },
  totalTopicStars: (topic: Topic) => {
    return config.starsPerQuiz * topic.quizzesCount;
  },
  isTopicComplited: (
    topics: any[],
    topicOrder: number,
    topicId: number,
    userProgress: any,
    unitId: number
  ): boolean => {
    if (!userProgress) {
      return topicOrder === 1;
    }

    const currentTopicProgress = userProgress.unit?.topicsOverviewPerUnit?.[unitId]?.[topicId];
    if (currentTopicProgress) {
      return true;
    }

    // For topic N to be unlocked, topics N-1 and N-2 (if they exist) must be completed
    const requiredPreviousTopics = Math.min(2, topicOrder - 1);

    if (requiredPreviousTopics === 0) {
      return true;
    }

    for (let i = 1; i <= requiredPreviousTopics; i++) {
      const previousTopicOrder = topicOrder - i;

      const previousTopic = topics.find(topic => topic.order === previousTopicOrder);
      if (!previousTopic) {
        continue;
      }

      const previousTopicProgress =
        userProgress.unit?.topicsOverviewPerUnit?.[unitId]?.[previousTopic.id];
      if (!previousTopicProgress || !previousTopicProgress.isAllActivitiesCompleted) {
        return false;
      }
    }

    return true;
  },
  detectSkippedActivities: (topics: any[], userProgress: any, isTeacher: boolean): boolean => {
    if (isTeacher || !topics.length || !userProgress) {
      return false;
    }

    const hasTopicSkippedActivities = (topic: any) => {
      const topicProgress = userProgress.topic?.activitiesOverviewPerTopic?.[topic.id];
      if (!topicProgress) return false;

      const totalActivities = topic.activitiesInTopic || 0;
      if (totalActivities === 0) return false;

      const activitiesWithProgress = Object.values(topicProgress)
        .filter((act: any) => act && act.order)
        .sort((a: any, b: any) => a.order - b.order) as any[];

      if (activitiesWithProgress.length >= 2) {
        for (let i = 0; i < activitiesWithProgress.length - 1; i++) {
          const current = activitiesWithProgress[i] as any;
          const next = activitiesWithProgress[i + 1] as any;
          if (next.order - current.order > 1) return true;
        }
      } else if (activitiesWithProgress.length === 1) {
        const single = activitiesWithProgress[0] as any;
        return single.order > 1 || single.order < totalActivities;
      }

      return false;
    };

    const isTopicCompleted = (topic: any) => {
      const topicProgress = userProgress.topic?.activitiesOverviewPerTopic?.[topic.id];
      if (!topicProgress) return false;
      const completedCount = Object.keys(topicProgress).length;
      const totalActivities = topic.activitiesInTopic || 0;
      return completedCount >= totalActivities;
    };

    for (const topic of topics) {
      if (hasTopicSkippedActivities(topic)) return true;
    }

    for (let i = 0; i < topics.length - 1; i++) {
      const currentTopic = topics[i];
      const nextTopic = topics[i + 1];

      const currentTopicProgress =
        userProgress.topic?.activitiesOverviewPerTopic?.[currentTopic.id];
      const nextTopicProgress = userProgress.topic?.activitiesOverviewPerTopic?.[nextTopic.id];

      if (currentTopicProgress && nextTopicProgress) {
        const isCurrentCompleted = isTopicCompleted(currentTopic);
        const hasNextProgress = Object.keys(nextTopicProgress).length > 0;

        if (!isCurrentCompleted && hasNextProgress) return true;
      }
    }

    return false;
  },
  /**
   * Returns the index in the topics array of the first topic that has skipped activities (gap).
   * Used to lock topics beyond "next topic after the one with gap" until user completes missing activities.
   * Returns null if no topic has skipped activities or if teacher/empty progress.
   */
  getFirstTopicWithSkippedActivitiesIndex: (
    topics: any[],
    userProgress: any,
    isTeacher: boolean
  ): number | null => {
    if (isTeacher || !topics.length || !userProgress) {
      return null;
    }

    const hasTopicSkippedActivities = (topic: any) => {
      const topicProgress = userProgress.topic?.activitiesOverviewPerTopic?.[topic.id];
      if (!topicProgress) return false;

      const totalActivities = topic.activitiesInTopic || 0;
      if (totalActivities === 0) return false;

      const activitiesWithProgress = Object.values(topicProgress)
        .filter((act: any) => act && act.order)
        .sort((a: any, b: any) => a.order - b.order) as any[];

      if (activitiesWithProgress.length >= 2) {
        for (let i = 0; i < activitiesWithProgress.length - 1; i++) {
          const current = activitiesWithProgress[i] as any;
          const next = activitiesWithProgress[i + 1] as any;
          if (next.order - current.order > 1) return true;
        }
      } else if (activitiesWithProgress.length === 1) {
        const single = activitiesWithProgress[0] as any;
        return single.order > 1 || single.order < totalActivities;
      }

      return false;
    };

    const isTopicCompleted = (topic: any) => {
      const topicProgress = userProgress.topic?.activitiesOverviewPerTopic?.[topic.id];
      if (!topicProgress) return false;
      const completedCount = Object.keys(topicProgress).length;
      const totalActivities = topic.activitiesInTopic || 0;
      return completedCount >= totalActivities;
    };

    for (let i = 0; i < topics.length; i++) {
      if (hasTopicSkippedActivities(topics[i])) return i;
    }

    for (let i = 0; i < topics.length - 1; i++) {
      const currentTopic = topics[i];
      const nextTopic = topics[i + 1];

      const currentTopicProgress =
        userProgress.topic?.activitiesOverviewPerTopic?.[currentTopic.id];
      const nextTopicProgress = userProgress.topic?.activitiesOverviewPerTopic?.[nextTopic.id];

      if (currentTopicProgress && nextTopicProgress) {
        const isCurrentCompleted = isTopicCompleted(currentTopic);
        const hasNextProgress = Object.keys(nextTopicProgress).length > 0;

        if (!isCurrentCompleted && hasNextProgress) return i;
      }
    }

    return null;
  },
  totalStars: (topic: Topic | null) => {
    return (topic?.quizzesCount ?? 0) * config.starsPerQuiz;
  },
  isCompleted: (activityId: number, topicId: number | undefined, topicProgress: any): boolean => {
    if (!activityId || !topicProgress || !topicId || !topicProgress[topicId]) {
      return false;
    }

    return (
      String(activityId) in topicProgress[topicId] &&
      topicProgress[topicId][activityId].isCompleted === true
    );
  },
  isSkipped: (activityId: number, topicId: number | undefined, topicProgress: any): boolean => {
    if (!activityId || !topicProgress || !topicId || !topicProgress[topicId]) {
      return false;
    }

    return (
      String(activityId) in topicProgress[topicId] &&
      topicProgress[topicId][activityId].isSkipped === true
    );
  },
  getLockedOrder: (
    quizesGroup: any[][] | undefined,
    topicId: number | undefined,
    user: UserState | null
  ): number => {
    if (!quizesGroup || topicId === undefined || !user) {
      return 0;
    }

    const progress = user.progress?.topic?.activitiesOverviewPerTopic;

    const topicProgress = progress?.[topicId];
    if (!topicProgress || Object.keys(topicProgress).length === 0) {
      return 0;
    }

    const allActivities = Object.values(quizesGroup).flat();
    const maxOrder = allActivities
      .filter(
        activity =>
          Object.prototype.hasOwnProperty.call(topicProgress, activity.id) &&
          topicProgress[activity.id].isCompleted
      )
      .reduce((max, activity) => Math.max(max, activity.order), 0);
    return maxOrder;
  },
  sortByKey: (arr: any, key: string) => {
    return arr.sort((a: any, b: any) => a[key] - b[key]);
  },
  generateRandomId: (length: number) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
  },
  formatText: (text: string) => {
    const italicRegex = /#i#(.*?)#\/i#|\<i\>(.*?)\<\/i\>/g;
    const boldRegex = /#b#(.*?)#\/b#|\<b\>(.*?)\<\/b\>/g;
    const boldRegex2 = /#b#(.*?)#b#/g;

    return text
      .replace(italicRegex, (_, match1, match2) => {
        return `<span style="font-style: italic">${match1 || match2}</span>`;
      })
      .replace(boldRegex, (_, match1, match2) => {
        return `<span style="font-weight: bold">${match1 || match2}</span>`;
      })
      .replace(boldRegex2, (_, match1, match2) => {
        return `<span style="font-weight: bold">${match1 || match2}</span>`;
      });
  },
  formatString: (template: string, ...args: any[]) => {
    return template.replace(/{([0-9]+)}/g, function (match, index) {
      return typeof args[index] === 'undefined' ? match : args[index];
    });
  },
  shouldTokenRefresh: (token: string) => {
    try {
      if (token === '') {
        return true;
      }
      // Decode the token to get the payload
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));

      // Check if the token has an expiration time
      if (!payload.exp) {
        console.error('Token does not contain expiration information.');
        return false;
      }

      // Get the current time in seconds since the Unix epoch
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if the token expires in less than 100 seconds
      return payload.exp - currentTime < 150;
    } catch (e) {
      console.error('Error decoding token:', e);
      return false;
    }
  },
  isTokenExpired: (token: string) => {
    try {
      if (token === '') {
        return true;
      }
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));

      if (!payload.exp) {
        return false;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp <= currentTime;
    } catch (e) {
      console.error('Error decoding token:', e);
      // If we cannot decode it, treat it as expired so callers fail closed.
      return true;
    }
  },
  calculateAchievedStars(activitiesOverviewPerTopic: any) {
    let result = 0;
    if (activitiesOverviewPerTopic == null || typeof activitiesOverviewPerTopic !== 'object') {
      return result;
    }

    const activityKeys = Object.keys(activitiesOverviewPerTopic);
    if (!activityKeys.length) {
      return result;
    } else {
      activityKeys.forEach(activity => {
        result += activitiesOverviewPerTopic[activity]?.cumulativeAchievedStars || 0;
      });

      return result;
    }
  },
  calculateComplitedActivities(activitiesOverviewPerTopic: any) {
    let result = 0;
    if (activitiesOverviewPerTopic == null || typeof activitiesOverviewPerTopic !== 'object') {
      return result;
    }

    const activityKeys = Object.keys(activitiesOverviewPerTopic);
    if (!activityKeys.length) {
      return result;
    } else {
      activityKeys.forEach(activity => {
        const activityData = activitiesOverviewPerTopic[activity];
        if (activityData && (activityData.cumulativeAchievedStars || activityData.isCompleted)) {
          ++result;
        }
      });

      return result;
    }
  },
  getRandomInt: (from: number, to: number) => {
    return Math.floor(Math.random() * (to - from + 1)) + from;
  },
  getAndRemoveRandomItem: (array: any[]) => {
    const randomIndex = commonUtils.getRandomInt(0, array.length - 1);
    const item = array[randomIndex];
    array.splice(randomIndex, 1);
    return item;
  },
  isSupportedLanguage: (languageCode: string | null): boolean => {
    if (!languageCode) return false;
    return supportedLanguages.includes(languageCode);
  },
  isGoogle: (user: UserState | null) => {
    return user?.metadata.type === 'google-classroom';
  },
  getInitials: (firstName?: string | null, lastName?: string | null) => {
    return [firstName, lastName]
      .filter(Boolean)
      .map(name => name!.trim()[0])
      .join('');
  },
  isClassCode: (user: UserState | null, schoolClassCodeEnabled?: boolean) => {
    const classCodeEnabledForUser = user?.metadata?.organizationDetails?.classCodeEnabled === true;
    const classCodeEnabledForSchool = schoolClassCodeEnabled === true;
    return classCodeEnabledForUser || classCodeEnabledForSchool;
  },
  isMGWebsite: () => {
    return config.isMGWebsite ? true : false;
  },
  handleDownloadExcel: (students: ClassOverviewData[]) => {
    if (!students || students.length === 0) return;

    const firstStudent = students[0];
    const useEmail = firstStudent.email && commonUtils.isEmail(firstStudent.email);
    const userKey = useEmail ? 'Email' : 'Username';

    const data = students.map(student => {
      return {
        'Student Name': formatPersonName(student.firstName, student.lastName),
        [userKey]: useEmail ? student.email : student.username,
        Status: student.userStatus ?? '',
        'Starting Level':
          student.levelTestResult && student.levelTestResult > 0 ? student.levelTestResult : '',
        'Current Level': student.level && student.level > 0 ? student.level : '',
        Progress:
          typeof student.progress?.activitiesProgress === 'number' &&
          !isNaN(student.progress?.activitiesProgress)
            ? `${student.progress.activitiesProgress}%`
            : '',
        'Time Spent':
          student.progress?.timeSpent !== undefined
            ? formatMinutes(student.progress.timeSpent)
            : '',
        Score: student.progress?.aVGScore ?? '',
        'Last Login': student.userSessionInfo?.lastLogin
          ? new Date(student.userSessionInfo.lastLogin).toLocaleDateString()
          : '',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'students-list.xlsx';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, 100);
  },
  /**
   * Strips zero-width / bidi control characters and all whitespace, then trims.
   * Use before validating or submitting email from user input.
   */
  sanitizeEmailInput: (email: string): string => {
    return email
      .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E\u2066-\u2069]/g, '')
      .replace(/\s+/g, '')
      .trim();
  },
  isEmail: (string: string) => {
    return Boolean(string) && EMAIL_FORMAT_REGEX.test(string);
  },
  checkMicrophoneAccess: async (): Promise<{ isAllowed: boolean; errorType?: string }> => {
    if (
      navigator.permissions &&
      navigator.permissions.query &&
      navigator.mediaDevices.enumerateDevices
    ) {
      try {
        const devices = (await navigator.mediaDevices.enumerateDevices()) || [];
        const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

        if (audioInputDevices.length === 0) {
          return { isAllowed: false, errorType: 'NotFoundError' };
        }

        const permissionStatus = await navigator.permissions.query({
          // eslint-disable-next-line no-undef
          name: 'microphone' as PermissionName,
        });

        if (permissionStatus.state === 'granted') {
          return { isAllowed: true };
        } else if (permissionStatus.state === 'denied') {
          return { isAllowed: false, errorType: 'permission-denied' };
        } else {
          return { isAllowed: false, errorType: 'permission-prompt' };
        }
      } catch (err) {
        console.warn('Permissions API error:', err);
        commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
      }
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return { isAllowed: true };
    } catch (err: any) {
      return {
        isAllowed: false,
        errorType: err?.name || 'getUserMedia-error',
      };
    }
  },
  requestMicrophoneAccess: async (): Promise<{
    isAllowed: boolean;
    errorType?: string;
  }> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      stream.getTracks().forEach(track => track.stop());

      return { isAllowed: true };
    } catch (err: any) {
      return {
        isAllowed: false,
        errorType: err?.name || 'unknown-error',
      };
    }
  },
  showGlobalError: ({
    title,
    message,
    i18nKey,
    silent,
    navigateOnClose,
  }: {
    title?: string;
    message?: string;
    i18nKey?: string;
    silent?: boolean;
    navigateOnClose?: string;
  }) => {
    store.dispatch(
      uiActions.showErrorModal({
        title,
        message,
        i18nKey,
        silent,
        navigateOnClose,
      })
    );
  },
  getBrowserData: () => {
    return {
      isAndroid: /Android/.test(navigator.userAgent),
      isEdge: /Edge/.test(navigator.userAgent) || /Edg/.test(navigator.userAgent),
      isFirefox: /Firefox/.test(navigator.userAgent),
      isChrome: /Google Inc/.test(navigator.vendor) && !/Edg/.test(navigator.userAgent),
      isChromeIOS: /CriOS/.test(navigator.userAgent),
      isIE: /Trident/.test(navigator.userAgent),
      isIOS: /(iPhone|iPad|iPod)/.test(navigator.platform),
      isOpera: /OPR/.test(navigator.userAgent),
      isSafari: /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent),
      isWebComponentsSupported:
        'registerElement' in document &&
        'import' in document.createElement('link') &&
        'content' in document.createElement('template'),
    };
  },
  filterLevels: (levels: LevelSettingItem[], t: TFunction) => {
    const dropdownOptions = levels
      .filter(item => item.id !== 10)
      .map(item => ({
        label: t(`levelName_${item.id}`),
        value: item.id,
      }));

    return dropdownOptions;
  },
  /**
   * Parse a date string in YYYY-MM-DD format as a local date (not UTC)
   * This prevents timezone issues where dates shift by one day
   * @param dateString - Date string in YYYY-MM-DD format
   * @returns Date object in local timezone
   */
  parseLocalDate: (dateString: string): Date => {
    const parsed = dayjs(dateString);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
    return new Date(dateString);
  },
};

export default commonUtils;
